import React from 'react'
import { Box, CircularProgress, Stack, Typography, alpha } from '@mui/material'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import DeleteIcon from '@mui/icons-material/Delete'

interface CustomDialogProps {
  readonly openCustomDialog: boolean
  readonly setOpenCustomDialog: () => void
  readonly labelHeader: string
  readonly label: string | React.ReactNode
  readonly leftButtonLabel: string
  readonly rightButtonLabel: string
  readonly onRightButtonClick?: () => void
  readonly iconType?: 'warning' | 'delete'
  /** When true: shows in-button spinner, disables both buttons, blocks backdrop/escape close. */
  readonly rightButtonLoading?: boolean
}

export default function CustomDialog(props: CustomDialogProps) {
  const {
    openCustomDialog,
    setOpenCustomDialog,
    labelHeader,
    label,
    leftButtonLabel,
    rightButtonLabel,
    onRightButtonClick,
    iconType = 'warning',
    rightButtonLoading = false,
  } = props

  const handleClose = () => {
    if (rightButtonLoading) return
    setOpenCustomDialog()
  }

  const handleRightButtonClick = () => {
    if (onRightButtonClick) {
      onRightButtonClick()
      // Don't call setOpenCustomDialog here - let the callback handle it
    } else {
      setOpenCustomDialog()
    }
  }

  const isDelete = iconType === 'delete'
  const accentColor = isDelete ? 'error.main' : '#F2930D'

  return (
    <Dialog
      open={openCustomDialog}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="xs"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 3 } } }}
    >
      <Stack alignItems="center" spacing={2} sx={{ px: 4, pt: 4, pb: 3.5, textAlign: 'center' }}>
        {/* Icon badge */}
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: accentColor,
            bgcolor: (theme) => alpha(isDelete ? theme.palette.error.main : '#F2930D', 0.12),
          }}
        >
          {isDelete ? (
            <DeleteIcon sx={{ fontSize: 28 }} />
          ) : (
            <WarningAmberOutlinedIcon sx={{ fontSize: 28 }} />
          )}
        </Box>

        {/* Title + message */}
        <Stack spacing={0.75} alignItems="center">
          <Typography
            id="alert-dialog-title"
            variant="bodyRegular3"
            color="grey.900"
            sx={{ fontWeight: 600 }}
          >
            {labelHeader}
          </Typography>
          <Typography
            id="alert-dialog-description"
            variant="bodyMedium2"
            color="grey.600"
            component={typeof label === 'string' ? 'p' : 'div'}
          >
            {label}
          </Typography>
        </Stack>

        {/* Actions */}
        <Stack direction="row" spacing={1.5} sx={{ width: '100%', mt: 1 }}>
          <Button fullWidth variant="outlined" onClick={handleClose} disabled={rightButtonLoading}>
            {leftButtonLabel}
          </Button>
          <Button
            fullWidth
            variant="contained"
            color={isDelete ? 'error' : 'primary'}
            onClick={handleRightButtonClick}
            disabled={rightButtonLoading}
          >
            {rightButtonLoading ? <CircularProgress size={20} color="inherit" /> : rightButtonLabel}
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  )
}
